import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell,
} from 'recharts';
import api from '../../apiClient';
import { getRevenueSummary, getRevenueMonthly, getRevenueDaily, getProposedActions } from '../../api/analyticsApi';
import { getActiveTenant } from '../../helpers/tenantHelpers';
import AiWorkedStrip from './AiWorkedStrip';
import FinishSetupBanner from '../../components/Setup/FinishSetupBanner';
// The never-configured list. FinishSetupBanner is the AI-inferred-and-unconfirmed
// list — different question, both can be true at once.
import OnboardingChecklist from '../../components/Setup/OnboardingChecklist';
import { computeKpiValues, formatKpi } from './kpiValues';
import AtRiskQueue, { AtRiskTile, useRetentionQueue } from './AtRiskQueue';
import { getIndustryCopy } from '../../constants/industryCopy';
import { getIndustry } from '../../helpers/tenantHelpers';
import './OperationsDashboard.css';

const CustomerPopup = lazy(() => import('../../components/Profile/CustomerPopup'));

/* Greeting: the first thing everyone sees. Reads the signed-in user's first
   name from the stored login payload; falls back to the plain page title when
   no name is stored (never a hardcoded person). */
const readFirstName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null') || {};
    const first = u.first_name || String(u.name || u.full_name || '').trim().split(' ')[0] || '';
    return String(first).trim();
  } catch { return ''; }
};
const greetingFor = (name) => {
  if (!name) return 'Dashboard';
  const h = new Date().getHours();
  const tod = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return `${tod}, ${name}`;
};

/* Home dashboard — daily operations + the marketing hero frame.
   Rules: no live-claim on synced data; no vendor names in copy; motion on
   mount only (reduced-motion collapses to final state); at rest the board
   is the screenshot — all interactivity dormant until touched. */

const money = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '$0';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};
const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* count-up on mount only — value changes after mount snap instantly */
function useCountUp(target, duration = 600, delay = 0) {
  // null-preserving: an unknown target (null) is NEVER animated toward 0 — it stays
  // null so the card renders "—". A real 0 animates to 0 and shows "$0".
  const [display, setDisplay] = useState(
    reducedMotion() || target === null || target === undefined ? target : 0,
  );
  const ran = useRef(false);
  const targetRef = useRef(target);
  targetRef.current = target; // ALWAYS animate toward the live value — a
  // stale closure here animated toward the mount-time 0 and stomped the
  // real number when data arrived fast (cache hydrate on nav-return): the
  // "$0 until you poke a card" bug.
  const animatingRef = useRef(false);
  useEffect(() => {
    if (ran.current || reducedMotion() || target === null || target === undefined) { setDisplay(target); return; }
    ran.current = true;
    animatingRef.current = true;
    let raf; const t0 = performance.now() + delay;
    const tick = (now) => {
      const p = Math.min(1, Math.max(0, (now - t0) / duration));
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      const tv = targetRef.current;
      // A target that becomes unknown mid-flight stops at "—", never snaps to 0.
      setDisplay(tv === null || tv === undefined ? tv : Number(tv) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else animatingRef.current = false;
    };
    raf = requestAnimationFrame(tick);
    return () => { animatingRef.current = false; cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (ran.current && !animatingRef.current) setDisplay(target); // null passes through → "—"
  }, [target]);
  return display;
}

function Sparkline({ points, color, width = 72, height = 26 }) {
  const [drawn, setDrawn] = useState(reducedMotion());
  useEffect(() => { const t = setTimeout(() => setDrawn(true), 60); return () => clearTimeout(t); }, []);
  if (!points || points.length < 2) return null;
  const min = Math.min(...points); const max = Math.max(...points);
  const span = max - min || 1;
  const pts = points.map((v, i) => `${(i / (points.length - 1)) * width},${height - 3 - ((v - min) / span) * (height - 6)}`).join(' ');
  return (
    <svg className="k-spark" width={width} height={height} aria-hidden="true">
      <polyline
        fill="none" stroke={color} strokeWidth="2" points={pts} pathLength="100"
        style={{ strokeDasharray: 100, strokeDashoffset: drawn ? 0 : 100, transition: 'stroke-dashoffset .8s ease-out' }}
      />
    </svg>
  );
}

function AiBadge({ tip }) {
  if (!tip) return null;
  return (
    <span className="ai-badge"><span className="d" />AI monitored<span className="tip">{tip}</span></span>
  );
}

/* KPI cards cycle WITHIN their own family only — families are disjoint, so
   two cards can never show the same metric. */
const CARD_FAMILIES = [
  ['active_mrr', 'arr', 'collected'],                              // revenue
  ['active_customers', 'total_customers', 'churned_customers'],    // customers
  ['churned_mrr', 'churn_pct'],                                    // churn
  ['paused_mrr', 'paused_count'],                                  // paused
];
const CMP_MODES = ['mom', 'qoq', 'yoy'];
const CMP_LABEL = { mom: 'vs last month', qoq: 'vs last quarter', yoy: 'vs last year' };
const DEFAULT_CARDS = CARD_FAMILIES.map(() => ({ idx: 0, cmp: 'mom' }));
const CARDS_LS_KEY = 'dash.kpiCards.v2';

export default function OperationsDashboard() {
  // C's retention queue — the tile and queue render only when the route exists.
  const retention = useRetentionQueue(8);
  const industryCopy = getIndustryCopy(getIndustry('general'));
  const [riskCustomer, setRiskCustomer] = useState(null);
  const queueRef = useRef(null);
  const navigate = useNavigate();
  const tenant = getActiveTenant() || {};
  const [metrics, setMetrics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [apptTotal, setApptTotal] = useState(0);
  const [opportunities, setOpportunities] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [range, setRange] = useState('12mo'); // 7d | 30d | 90d | 12mo
  const [filter, setFilter] = useState(null); // {type:'month', value, label}
  const [cards, setCards] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(CARDS_LS_KEY));
      return Array.isArray(s) && s.length === 4 && s.every(c => Number.isInteger(c?.idx)) ? s : DEFAULT_CARDS;
    } catch { return DEFAULT_CARDS; }
  });

  useEffect(() => { try { localStorage.setItem(CARDS_LS_KEY, JSON.stringify(cards)); } catch { /* ignore */ } }, [cards]);

  /* PERF: hydrate instantly from the last visit's snapshot (10-min TTL,
     per tenant), then refresh in the background — home nav feels instant
     and the 12mo series (which changes slowly) never blocks first paint. */
  const CACHE_KEY = `dash.snapshot.${tenant?.id || 't'}`;
  useEffect(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (c && Date.now() - c.at < 10 * 60e3) {
        if (c.metrics) setMetrics(c.metrics);
        if (c.summary) setSummary(c.summary);
        if (c.monthly) setMonthly(c.monthly);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    Promise.allSettled([
      api.get('/dashboard/metrics/'),
      getRevenueSummary(),
      getRevenueMonthly({ basis: 'payments' }),
      api.get('/scheduling/schedules/', { params: { page_size: 30 } }),
      api.get('/opportunities/', { params: { page_size: 12 } }),
      getProposedActions(),
    ]).then(([m, s, mo, sch, opp, pa]) => {
      if (!live) return;
      if (m.status === 'fulfilled') setMetrics(m.value.data || {});
      if (s.status === 'fulfilled') setSummary(s.value.data || {});
      if (mo.status === 'fulfilled') setMonthly(mo.value.data?.results || []);
      if (sch.status === 'fulfilled') {
        const d = sch.value.data || {};
        setAppointments(d.results || d || []);
        setApptTotal(Number(d.count || (d.results || []).length || 0));
      }
      if (opp.status === 'fulfilled') {
        const d = opp.value.data || {};
        setOpportunities(d.results || (Array.isArray(d) ? d : []));
      }
      if (pa.status === 'fulfilled') setProposals(pa.value.data?.proposed_actions || []);
      // transient failure → one delayed retry instead of silently showing zeros
      if (m.status === 'rejected' || s.status === 'rejected') {
        setTimeout(() => {
          if (!live) return;
          if (m.status === 'rejected') api.get('/dashboard/metrics/').then(r => live && setMetrics(r.data || {})).catch(() => {});
          if (s.status === 'rejected') getRevenueSummary().then(r => live && setSummary(r.data || {})).catch(() => {});
        }, 1500);
      }
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          at: Date.now(),
          metrics: m.status === 'fulfilled' ? m.value.data : null,
          summary: s.status === 'fulfilled' ? s.value.data : null,
          monthly: mo.status === 'fulfilled' ? (mo.value.data?.results || []) : null,
        }));
      } catch { /* quota — ignore */ }
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (range === '12mo') return;
    const days = { '7d': 7, '30d': 30, '90d': 90 }[range] || 30;
    let live = true;
    getRevenueDaily({ days, basis: 'payments' }).then(r => { if (live) setDaily(r.data?.results || []); }).catch(() => {});
    return () => { live = false; };
  }, [range]);

  /* ── series + derived numbers ── */
  const monthSeries = useMemo(() => (monthly || []).map(r => ({
    key: String(r.month || r.revenue_date || '').slice(0, 7),
    collected: Number(r.total_revenue || 0),
  })).filter(r => r.key).sort((a, b) => a.key.localeCompare(b.key)), [monthly]);

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = (key) => MONTH_NAMES[Number(String(key).slice(5, 7)) - 1] || key;

  /* Continuous, zero-filled bucket scaffold — the axis is built from the
     calendar, not from whatever rows the API returned, so data can never
     cluster left or grow ghost labels. Current period is always rightmost.
     7d/30d = daily buckets, 90d = weekly, 12mo = monthly. */
  const chartData = useMemo(() => {
    const today = new Date();
    const dayKey = (d) => d.toISOString().slice(0, 10);
    if (range === '12mo') {
      const mrr = Number(summary?.mrr || 0);
      const byKey = Object.fromEntries(monthSeries.map(r => [r.key, r.collected]));
      const buckets = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        buckets.push({ key, label: monthName(key), collected: Number(byKey[key] || 0), mrr: mrr || null, hot: i === 0 });
      }
      return buckets;
    }
    const dailyByKey = {};
    for (const r of (daily || [])) {
      const k = String(r.date || r.revenue_date || '').slice(0, 10);
      if (k) dailyByKey[k] = (dailyByKey[k] || 0) + Number(r.total_revenue || 0);
    }
    if (range === '90d') {
      const buckets = [];
      for (let w = 12; w >= 0; w--) {
        const end = new Date(today); end.setDate(end.getDate() - w * 7);
        const start = new Date(end); start.setDate(start.getDate() - 6);
        let sum = 0;
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) sum += dailyByKey[dayKey(d)] || 0;
        buckets.push({
          key: dayKey(end),
          label: `${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
          collected: sum, mrr: null, hot: w === 0,
        });
      }
      return buckets;
    }
    const n = range === '7d' ? 7 : 30;
    const buckets = [];
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const k = dayKey(d);
      buckets.push({
        key: k,
        label: n === 7 ? d.toLocaleDateString(undefined, { weekday: 'short' }) : k.slice(5),
        collected: Number(dailyByKey[k] || 0), mrr: null, hot: i === 0,
      });
    }
    return buckets;
  }, [range, monthSeries, daily, summary]);

  const collectedDelta = useMemo(() => {
    const s = monthSeries.slice(-13).map(r => r.collected);
    const cur = s[s.length - 1] || 0;
    const back = { mom: s[s.length - 2], qoq: s[s.length - 4], yoy: s[s.length - 13] };
    const out = {};
    for (const k of CMP_MODES) {
      const b = back[k];
      out[k] = b ? ((cur - b) / b) * 100 : null;
    }
    return out;
  }, [monthSeries]);

  const openProposal = proposals[0] || null;
  const aiWatchTip = openProposal
    ? `Watching ${openProposal?.evidence?.cohort_size ?? '—'} at-risk customers` +
      (openProposal?.evidence?.avg_risk_score ? ` (avg risk ${(openProposal.evidence.avg_risk_score * 100).toFixed(0)}%)` : '') +
      ' — win-back proposal open. Click AI Proposals to review.'
    : null;

  const kpiDefs = useMemo(() => {
    // Null-preserving: unknown is never 0. A real 0 stays 0 (animates to $0); an
    // absent source is null → "—". See kpiValues.js (unit-tested both cases/tile).
    const thisMonthKey = new Date().toISOString().slice(0, 7);
    // collected this month: the found row (a real 0 is a real month with no
    // revenue), or null when there is no month data at all — NOT 0.
    const collectedCur = monthSeries.length
      ? (monthSeries.find(r => r.key === thisMonthKey)?.collected
         ?? monthSeries[monthSeries.length - 1]?.collected ?? null)
      : null;
    const kv = computeKpiValues(summary, metrics, collectedCur);
    const bookKnown = kv.active_mrr !== null && kv.churned_mrr !== null && kv.paused_mrr !== null;
    const book = bookKnown ? kv.active_mrr + kv.churned_mrr + kv.paused_mrr : null;
    const shareSub = (part) => (book && book > 0 && part !== null) ? `${((part / book) * 100).toFixed(1)}% of book` : null;
    return {
      active_mrr: { label: 'Active MRR', value: kv.active_mrr, to: '/customers?agreements=active', ai: aiWatchTip,
        sub: summary?.active_agreements == null ? '—' : `${Number(summary.active_agreements).toLocaleString()} active agreements`,
        spark: monthSeries.slice(-8).map(r => r.collected), color: 'var(--dash-pos)' },
      arr: { label: 'Active ARR', value: kv.arr, to: '/customers?agreements=active', ai: aiWatchTip,
        sub: 'annualized from current MRR',
        spark: monthSeries.slice(-8).map(r => r.collected), color: 'var(--dash-pos)' },
      collected: { label: 'Collected — This Month', value: kv.collected, to: '/analytics',
        deltaModes: collectedDelta,
        spark: monthSeries.slice(-8).map(r => r.collected), color: 'var(--dash-bar)' },
      active_customers: { label: 'Active Customers', value: kv.active_customers, to: '/customers?status=active',
        sub: 'currently on service', spark: null, color: 'var(--dash-pos)', plain: true },
      total_customers: { label: 'Total Customers',
        value: kv.total_customers,
        to: '/customers', sub: 'active + inactive, all time', spark: null, color: 'var(--dash-pos)', plain: true },
      churned_customers: { label: 'Churned Customers', value: kv.churned_customers,
        to: '/customers?status=canceled', sub: 'inactive accounts', subTone: 'down', spark: null, color: 'var(--dash-neg)', plain: true },
      churn_pct: { label: 'Churn Rate', value: kv.churn_pct, to: '/customers?status=canceled',
        sub: 'churned MRR share of book', subTone: 'down', spark: null, color: 'var(--dash-neg)', pctFmt: true },
      paused_count: { label: 'Paused Agreements', value: kv.paused_count,
        to: '/customers?agreements=paused', sub: 'frozen / on hold', spark: null, color: 'var(--dash-muted-line)', plain: true },
      churned_mrr: { label: 'Churned MRR', value: kv.churned_mrr, to: '/customers?agreements=churned', ai: aiWatchTip,
        sub: shareSub(kv.churned_mrr), subTone: 'down',
        spark: null, color: 'var(--dash-neg)' },
      paused_mrr: { label: 'Paused MRR', value: kv.paused_mrr, to: '/customers?agreements=paused',
        sub: shareSub(kv.paused_mrr) || '— steady',
        spark: null, color: 'var(--dash-muted-line)' },
    };
  }, [summary, metrics, monthSeries, collectedDelta, aiWatchTip]);

  const cycleMetric = (i) => setCards(cs => cs.map((c, j) => {
    if (j !== i) return c;
    return { ...c, idx: (c.idx + 1) % CARD_FAMILIES[i].length };
  }));
  const cycleCmp = (i) => setCards(cs => cs.map((c, j) => {
    if (j !== i) return c;
    return { ...c, cmp: CMP_MODES[(CMP_MODES.indexOf(c.cmp) + 1) % CMP_MODES.length] };
  }));

  /* ── agreement donut ── */
  const activeAgr = Number(summary?.active_agreements || 0);
  const inactiveAgr = Number(summary?.inactive_agreements || 0);
  const donutData = [
    { name: 'Active', value: activeAgr, color: 'var(--dash-pos)', cohort: 'active' },
    { name: 'Frozen', value: inactiveAgr, color: 'var(--dash-warn)', cohort: 'inactive' },
  ].filter(d => d.value > 0);
  const agrTotal = activeAgr + inactiveAgr;

  /* ── funnel (real counts from existing endpoints) ── */
  // Same-window funnel (backend counts everything over the SAME trailing
  // 30 days) — a funnel can only narrow. Falls back to hiding percentages
  // if the window payload is absent (older API).
  const fw = metrics?.funnel_windows?.[range] || metrics?.funnel_30d || null;
  const funnel = fw ? {
    open: Number(fw.leads || 0), quoted: Number(fw.quotes || 0), booked: Number(fw.booked || 0), windowed: true,
  } : {
    open: Number(metrics?.leads_active || 0), quoted: Number(metrics?.open_quotes || 0), booked: 0, windowed: false,
  };
  const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : null);

  /* ── queues + appointments (cross-filterable) ── */
  const queueItems = useMemo(() => {
    let items = (opportunities || [])
      .map(o => ({
        who: o.customer_name || o.customer?.name || '',
        what: (o.opportunity_type || o.title || '').replace(/_/g, ' '),
        value: Number(o.estimated_value || o.value || 0),
        at: o.created_at ? new Date(o.created_at) : null,
        month: o.created_at ? String(o.created_at).slice(0, 7) : null,
      }))
      .filter(o => o.who);
    if (filter?.type === 'month') items = items.filter(o => o.month === filter.value);
    return items.slice(0, 5);
  }, [opportunities, filter]);

  const upcoming = useMemo(() => {
    // Source of record: metrics.upcoming_appointments (FR-aware, one round
    // trip). Falls back to the CRM schedules fetch on older API payloads.
    const src = Array.isArray(metrics?.upcoming_appointments) && metrics.upcoming_appointments.length
      ? metrics.upcoming_appointments.map(a => ({
          scheduled_start: a.scheduled_start,
          customer_name: a.customer_name,
          assigned_technician_name: a.technician_name,
          service_type: a.service,
        }))
      : (appointments || []);
    const now = Date.now();
    let items = src
      .filter(a => a.scheduled_start && new Date(a.scheduled_start).getTime() >= now - 3600e3)
      .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
    if (filter?.type === 'month') items = items.filter(a => String(a.scheduled_start).slice(0, 7) === filter.value);
    return items.slice(0, 4);
  }, [metrics, appointments, filter]);

  const ageOf = (d) => {
    if (!d) return '';
    const h = Math.max(0, (Date.now() - d.getTime()) / 3600e3);
    if (h < 24) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  };
  const ageClass = (d) => {
    if (!d) return 'ok';
    const days = (Date.now() - d.getTime()) / 86400e3;
    return days > 4 ? 'hot' : days > 1 ? 'warm' : 'ok';
  };

  const zeroCounts = ['hot_leads', 'stale_leads', 'website_leads_pending']
    .map(k => ({ k, n: Number(metrics?.[k] || 0) }));
  const allClear = zeroCounts.every(z => z.n === 0);

  const fmtWhen = (iso) => {
    const d = new Date(iso);
    const today = new Date(); const tmrw = new Date(); tmrw.setDate(today.getDate() + 1);
    const day = d.toDateString() === today.toDateString() ? 'Today'
      : d.toDateString() === tmrw.toDateString() ? 'Tomorrow'
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${day} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  };

  const hasPaymentsFeed = metrics?.packs ? Boolean(metrics.packs.payments_feed)
    : monthSeries.some(r => r.collected > 0);
  const summaryForecast = summary?.sales_flow_forecast || {};
  const projMrr = Number(summaryForecast?.projected_mrr || 0);
  const projBooked = Number(summaryForecast?.projected_booked_revenue_30_day || 0);

  return (
    <div className="dash">
      <FinishSetupBanner />
      <OnboardingChecklist />
      <div className="dash-hdr">
        <div>
          <h1>{greetingFor(readFirstName())}</h1>
          <div className="dash-sub">{tenant?.name ? `${tenant.name} — ` : ''}revenue, field execution, and retention at a glance</div>
        </div>
        <div className="dash-hdr-right">
          <div className="dash-range" role="tablist" aria-label="Time range">
            {['7d', '30d', '90d', '12mo'].map(r => (
              <button key={r} className={range === r ? 'on' : ''} onClick={() => setRange(r)}>{r}</button>
            ))}
          </div>
          {proposals.length > 0 && (
            <div className="dash-ai-entry" onClick={() => navigate('/ai/proposals')} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && navigate('/ai/proposals')}>
              <span className="dash-ai-spark" />
              <span><b>AI Proposals ({proposals.length})</b>{openProposal ? ` · ${openProposal.title}` : ''}</span>
              <span className="chev">›</span>
            </div>
          )}
        </div>
      </div>

      <AiWorkedStrip />

      {filter && (
        <div style={{ marginBottom: 12 }}>
          <span className="dash-filter-chip">
            Filtered: {filter.label}
            {filter.type === 'month' && (
              <Link to={`/finance?month=${filter.value}`} style={{ color: 'inherit' }}>view transactions →</Link>
            )}
            <button onClick={() => setFilter(null)} aria-label="Clear filter">×</button>
          </span>
        </div>
      )}

      {/* KPI band */}
      <div className={`dash-kpis ${!summary && !metrics ? 'dash-skel' : ''}`}>
        {cards.map((c, i) => {
          const metric = CARD_FAMILIES[i][c.idx % CARD_FAMILIES[i].length];
          const def = kpiDefs[metric] || kpiDefs.active_mrr;
          const deltaPct = def.deltaModes ? def.deltaModes[c.cmp] : null;
          return (
            <KpiCard key={`${metric}-${i}`} index={i} def={def} cmp={c.cmp}
              deltaPct={deltaPct} onCycleMetric={() => cycleMetric(i)}
              onCycleCmp={def.deltaModes ? () => cycleCmp(i) : null} />
          );
        })}
        <AtRiskTile queue={retention.queue} copy={industryCopy} onOpen={() => queueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      </div>

      <div className="dash-grid">
        {/* Revenue chart */}
        <div className="dash-card dash-chart-wrap">
          <div className="dash-title">Collected Revenue
            <span className="dash-note">{range === '12mo' ? '12 months · bars = collected · line = current MRR level' : `last ${range} · daily collected`}</span>
          </div>
          <div className="dash-note" style={{ marginBottom: 8 }}>Collected · net of refunds</div>
          {!hasPaymentsFeed ? (
            <div className="dash-empty" style={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              No payment data yet — revenue appears here once a billing feed is connected.
            </div>
          ) : (
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: 'var(--text-3)', fontSize: 10 }} />
              <YAxis hide domain={[0, 'dataMax + 12000']} />
              <Tooltip cursor={{ fill: 'var(--accent-dim)' }} formatter={(v, n) => [money(v), n === 'collected' ? 'Collected' : 'MRR']}
                contentStyle={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12 }}
                labelStyle={{ color: 'var(--text-2)' }} itemStyle={{ color: 'var(--text-1)' }} />
              <Bar dataKey="collected" radius={[5, 5, 2, 2]} animationDuration={reducedMotion() ? 0 : 700} animationEasing="ease-out"
                onClick={(d) => d?.key && range === '12mo' && setFilter({ type: 'month', value: d.key, label: d.key })}
                cursor={range === '12mo' ? 'pointer' : 'default'}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.hot ? 'var(--dash-bar-hot)' : 'var(--dash-bar)'} />
                ))}
              </Bar>
              {range === '12mo' && (
                <Line dataKey="mrr" stroke="var(--dash-mrr-line)" strokeWidth={2.5} dot={false}
                  animationDuration={reducedMotion() ? 0 : 800} animationEasing="ease-out" />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          )}
        </div>

        <div className="dash-rcol">
          {/* Agreement health donut */}
          <div className="dash-card dash-chart-wrap">
            <div className="dash-title">Agreement Health<AiBadge tip={aiWatchTip} /></div>
            {agrTotal > 0 ? (
              <div className="donut-row">
                <PieChart width={118} height={118}>
                  <Pie data={donutData} dataKey="value" innerRadius={38} outerRadius={52} strokeWidth={0}
                    startAngle={90} endAngle={-270} animationDuration={reducedMotion() ? 0 : 800} animationEasing="ease-out"
                    onClick={(d) => d?.cohort && navigate(`/customers?agreements=${d.cohort}`)}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} cursor="pointer" />)}
                  </Pie>
                </PieChart>
                <div className="dlegend">
                  {donutData.map(d => (
                    <div key={d.name} onClick={() => navigate(`/customers?agreements=${d.cohort}`)}>
                      <span className="sw" style={{ background: d.color }} />
                      {d.name} <b>{d.value.toLocaleString()}</b> · {Math.round((d.value / agrTotal) * 100)}%
                    </div>
                  ))}
                  <div className="quiet">{agrTotal.toLocaleString()} agreements total</div>
                  <div><Link to="/customers" style={{ color: 'var(--accent)', fontSize: 11 }}>Open customer list →</Link></div>
                </div>
              </div>
            ) : <div className="dash-empty">No agreements yet</div>}
          </div>

          {/* Funnel */}
          <div className="dash-card">
            <div className="dash-title">Sales Funnel<span className="dash-note">new-lead cohort · last {range}</span></div>
            <div className="funnel">
              <Link className="fstage" to="/leads"><div className="n">{funnel.open}</div><div className="l">Open Leads</div></Link>
              <span className="farrow">{pct(funnel.quoted, funnel.open) != null ? `${pct(funnel.quoted, funnel.open)}% →` : '→'}</span>
              <Link className="fstage" to="/quotes"><div className="n">{funnel.quoted}</div><div className="l">Quoted</div></Link>
              <span className="farrow">{pct(funnel.booked, funnel.quoted) != null ? `${pct(funnel.booked, funnel.quoted)}% →` : '→'}</span>
              <Link className="fstage" to="/schedule"><div className="n">{funnel.booked}</div><div className="l">Booked</div></Link>
            </div>
            {(projMrr > 0 || projBooked > 0) && (
              <div className="qzero">
                {projBooked > 0 && <>Projected 30-day booked <b>{money(projBooked)}</b>&nbsp;&nbsp;</>}
                {projMrr > 0 && <>Projected MRR <b>{money(projMrr)}</b></>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dash-queues">
        {/* Needs Action */}
        <div className="dash-card">
          <div className="dash-title">Needs Action
            <span className="dash-note">{queueItems.length ? `${queueItems.length} item${queueItems.length === 1 ? '' : 's'}` : ''}</span>
          </div>
          {queueItems.length ? queueItems.map((q, i) => (
            <div className="qrow" key={i}>
              <span className={`qage ${ageClass(q.at)}`} />
              <span className="who">{q.who}</span>
              <span>{q.what}{q.value > 0 ? ` · ${money(q.value)}` : ''}</span>
              <span className="when">{ageOf(q.at)}</span>
            </div>
          )) : (
            <div className="dash-empty">{filter ? `No queue items match “${filter.label}”` : 'Queue clear'}</div>
          )}
          <div className="qzero">
            {allClear
              ? <>Hot <b>0</b> · Stale <b>0</b> · Website pending <b>0</b> — all clear</>
              : zeroCounts.filter(z => z.n > 0).map(z => (
                  <span key={z.k} style={{ marginRight: 12 }}>{z.k.replace(/_/g, ' ')} <b>{z.n}</b></span>
                ))}
          </div>
        </div>

        {/* Upcoming appointments */}
        <div className="dash-card">
          <div className="dash-title">Upcoming Appointments</div>
          {upcoming.length ? upcoming.map((a, i) => (
            <div className="appt-row" key={a.id || i}>
              <span className="t">{fmtWhen(a.scheduled_start)}</span>
              <div>
                <div className="nm">{a.customer_name || 'Customer'}</div>
                <div className="svc">{[a.service_type, a.assigned_technician_name || a.assigned_to_name].filter(Boolean).join(' · ') || 'Details pending'}</div>
              </div>
            </div>
          )) : (
            <div className="dash-empty">{filter ? `No appointments in ${filter.label}` : 'Nothing scheduled yet'}</div>
          )}
        </div>
      </div>

      {retention.queue ? (
        <div className="dash-card" ref={queueRef}>
          <AtRiskQueue queue={retention.queue} loading={retention.loading} copy={industryCopy} onOpenCustomer={(c) => setRiskCustomer(c)} />
        </div>
      ) : null}
      {riskCustomer ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={{ object: 'customer', id: riskCustomer.id, customer_id: riskCustomer.id, display_name: riskCustomer.name }}
            leadType="customer"
            initialTab="ai_insights"
            onClose={() => setRiskCustomer(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

function KpiCard({ index, def, cmp, deltaPct, onCycleMetric, onCycleCmp }) {
  const animated = useCountUp(def.value, 600, index * 80);
  // null/unknown → "—"; a real number formats (a real 0 → "$0" / "0" / "0.0%").
  const shown = formatKpi(animated, def);
  return (
    <div className="dash-card dash-kpi">
      <div className="k-label">
        {def.label}
        <button className="k-cycle" title="Switch metric" onClick={onCycleMetric}>⟳</button>
        <span style={{ marginLeft: 'auto' }}><AiBadge tip={def.ai} /></span>
      </div>
      <div className="k-value"><Link to={def.to}>{shown}</Link></div>
      {deltaPct != null ? (
        <div className={`k-delta ${deltaPct >= 0 ? 'up' : 'down'}`} onClick={onCycleCmp}
          style={onCycleCmp ? { cursor: 'pointer' } : undefined}
          title={onCycleCmp ? 'Switch comparison period' : undefined}>
          {deltaPct >= 0 ? '▲' : '▼'} {Math.abs(deltaPct).toFixed(1)}% {CMP_LABEL[cmp]}
        </div>
      ) : (
        <div className={`k-delta ${def.subTone || ''}`}>{def.sub || '—'}</div>
      )}
      {def.spark && def.spark.length > 1 && <Sparkline points={def.spark} color={def.color} />}
    </div>
  );
}
